'use client';

import { m } from 'motion/react';
import { useRef, useState } from 'react';
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion';

/**
 * Scribble underline SVG drawn under the italic-serif hero accent.
 * Absolutely positioned so it doesn't affect the inline text flow.
 * The path is a hand-drawn-feeling cubic bezier with uneven curvature —
 * three control points making it feel organic rather than geometric.
 * Redraws on parent hover via key remount (cheapest keyframe replay).
 */
export function HeroAccentUnderline({ replay }: { replay?: boolean }) {
  const reduced = usePrefersReducedMotion();
  // Use a key derived from `replay` to force remount and restart the animation
  // when the parent reports hover state changed.
  const replayKey = replay ? 'hover' : 'default';

  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 -bottom-[5px] h-[8px] block"
    >
      <svg
        viewBox="0 0 200 8"
        preserveAspectRatio="none"
        className="h-full w-full"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <m.path
          key={replayKey}
          /*
           * Three control points with deliberate asymmetry:
           * starts slightly above center, dips below, rises at the end.
           * This is the "written by hand" quality — a perfect arc would
           * feel generated; slight tension at start and finish reads as ink.
           */
          d="M 2 5 C 30 2, 80 8, 120 4 S 170 7, 198 3"
          stroke="var(--color-accent)"
          strokeWidth="2"
          strokeLinecap="round"
          initial={reduced ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0.85 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={
            reduced
              ? { duration: 0 }
              : {
                  pathLength: {
                    duration: 0.6,
                    delay: 1.1,
                    ease: [0.16, 1, 0.3, 1],
                  },
                  opacity: {
                    duration: 0.15,
                    delay: 1.1,
                  },
                }
          }
        />
      </svg>
    </span>
  );
}

/**
 * Wraps a slot with hover-tracking so the underline can redraw on hover.
 * This is the only piece that needs state; the SVG itself is stateless.
 */
export function HeroAccentUnderlineHost({ children }: { children: React.ReactNode }) {
  // Track each hover-in as a flag flip so the underline animation replays.
  const replayRef = useRef(false);
  const [replayFlag, setReplayFlag] = useState(false);

  const handleMouseEnter = () => {
    replayRef.current = !replayRef.current;
    setReplayFlag(replayRef.current);
  };

  return (
    <span
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
    >
      {children}
      <HeroAccentUnderline replay={replayFlag} />
    </span>
  );
}
